<template>
  <label :class="checkboxClasses">
    <input
      type="checkbox"
      :checked="isChecked"
      :disabled="disabled"
      :indeterminate="indeterminate"
      class="checkbox__input"
      @change="onChange"
    />
    <span class="checkbox__box">
      <svg v-if="isChecked && !indeterminate" class="checkbox__icon" viewBox="0 0 24 24">
        <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
      </svg>
      <svg v-if="indeterminate" class="checkbox__icon" viewBox="0 0 24 24">
        <path fill="currentColor" d="M19 13H5v-2h14v2z"/>
      </svg>
    </span>
    <span v-if="label || $slots.default" class="checkbox__label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script>
export default {
  name: 'BaseCheckbox',
  props: {
    modelValue: {
      type: [Boolean, Array],
      default: false
    },
    value: {
      type: [String, Number, Object],
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    indeterminate: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'primary'
    }
  },
  emits: ['update:modelValue', 'change'],
  computed: {
    isChecked() {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue.includes(this.value)
      }
      return this.modelValue
    },
    checkboxClasses() {
      return [
        'checkbox',
        `checkbox--${this.color}`,
        {
          'checkbox--checked': this.isChecked,
          'checkbox--disabled': this.disabled,
          'checkbox--indeterminate': this.indeterminate
        }
      ]
    }
  },
  methods: {
    onChange(e) {
      let newValue
      if (Array.isArray(this.modelValue)) {
        newValue = [...this.modelValue]
        if (e.target.checked) {
          newValue.push(this.value)
        } else {
          const index = newValue.indexOf(this.value)
          if (index > -1) newValue.splice(index, 1)
        }
      } else {
        newValue = e.target.checked
      }
      this.$emit('update:modelValue', newValue)
      this.$emit('change', newValue)
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.checkbox {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 2px solid $color-border-default;
    border-radius: $radius-default;
    background-color: $color-bg-primary;
    transition: all $transition-fast;
  }

  &__icon {
    width: 14px;
    height: 14px;
    color: $color-text-inverse;
  }

  &__label {
    margin-left: $spacing-2;
    font-size: $font-size-base;
    color: $color-text-primary;
  }

  // Color variants
  &--primary.checkbox--checked .checkbox__box,
  &--primary.checkbox--indeterminate .checkbox__box {
    background-color: $color-primary;
    border-color: $color-primary;
  }

  &--success.checkbox--checked .checkbox__box,
  &--success.checkbox--indeterminate .checkbox__box {
    background-color: $color-success;
    border-color: $color-success;
  }

  &--danger.checkbox--checked .checkbox__box,
  &--danger.checkbox--indeterminate .checkbox__box {
    background-color: $color-danger;
    border-color: $color-danger;
  }

  &:hover:not(.checkbox--disabled) .checkbox__box {
    border-color: $color-primary;
  }

  &:focus-within .checkbox__box {
    box-shadow: 0 0 0 3px $input-focus-ring-color;
  }
}
</style>
